<template>
  <div class="event-form--content">
    <p>Choose the dates & times for your event. The times apply to each day selected.</p>
    <div class="form-row form-row--event-datetime">
      <div class="form-cell">
        <FloatLabel variant="on" class="has-icon">
          <IconField iconPosition="left">
            <InputIcon>
              <IconCalendar />
            </InputIcon>
            <DatePicker
              v-model="eventDates"
              :minDate="minDate"
              :maxDate="maxDate"
              selectionMode="range"
              :manualInput="false"
              @date-select="updateDates"
            />
          </IconField>
          <label>Dates *</label>
        </FloatLabel>
      </div>

      <div class="form-cell">
        <FloatLabel variant="on">
          <DatePicker
            id="event_from"
            v-model="eventFrom"
            timeOnly
            :manualInput="false"
            hourFormat="12"
            :stepMinute="15"
            @date-select="updateDates"
          />
          <label for="event_from">Start Time *</label>
        </FloatLabel>
      </div>

      <div class="form-cell">
        <FloatLabel variant="on">
          <DatePicker
            id="event_to"
            v-model="eventTo"
            timeOnly
            :manualInput="false"
            hourFormat="12"
            :stepMinute="15"
            @date-select="updateDates"
          />
          <label for="event_to">End Time *</label>
        </FloatLabel>
      </div>
    </div>

    <template v-if="!sameDates && hasDates">
      <div class="exceptions-section">
        <div class="event-form--section-title">
          <span>Schedule by Date</span>
        </div>

        <p>
          You can override specific days and times below to help you streamline your insights to
          only days and times that are relevant. If you make changes above, these overrides will
          reset.
        </p>

        <ul class="date-overrides" :class="{ 'date-overrides--overnight': anyOvernight }">
          <li class="date-overrides__header">
            <span></span>
            <span><template v-if="anyOvernight">Start </template>Date</span>
            <span>Start Time</span>
            <span v-if="anyOvernight">End Date</span>
            <span>End Time</span>
          </li>
          <li v-for="(date, index) in newEvent?.dates" :key="index">
            <span>
              <Checkbox v-model="dateIncludes" :value="date.day" />
            </span>

            <span>{{ dayjs(date.day).format('ddd, MMM DD, YYYY') }}</span>

            <DatePicker
              v-model="date.from"
              hourFormat="12"
              timeOnly
              :stepMinute="15"
              @value-change="checkOvernight(date)"
            />

            <span v-if="anyOvernight">
              <template v-if="date.isDateOvernight">
                {{ dayjs(date.day).add(1, 'day').format('ddd, MMM DD, YYYY') }}
              </template>
            </span>

            <DatePicker
              v-model="date.to"
              hourFormat="12"
              timeOnly
              :stepMinute="15"
              @value-change="checkOvernight(date)"
            />
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';

import { Checkbox, DatePicker, FloatLabel, IconField, InputIcon } from 'primevue';

import type { EventDate, EventTypeNew } from '@/types/Event';

import IconCalendar from '@/icons/events/IconCalendar.svg?component';

const eventDates = ref<Date[]>([]);
const newEvent = defineModel<EventTypeNew>('newEvent');

const eventFrom = ref<Date>(dayjs().hour(9).minute(0).toDate());
const eventTo = ref<Date>(dayjs().hour(17).minute(0).toDate());

const minDate = dayjs().toDate();
const maxDate = dayjs().add(3, 'months').toDate();

const includedDays = ref<string[]>();
const dateIncludes = defineModel<string[]>('dateIncludes');

if (newEvent.value?.event_start) {
  if (newEvent.value.event_end) {
    eventDates.value = [
      dayjs(newEvent.value?.event_start).toDate(),
      dayjs(newEvent.value?.event_end).toDate(),
    ];
  }
  eventFrom.value = dayjs(newEvent.value?.event_start).toDate();
  eventTo.value = dayjs(newEvent.value?.event_end ?? newEvent.value.event_start).toDate();
}

const hasDates = computed(() => {
  return eventDates.value && eventDates.value[0] != null && eventDates.value[1] != null;
});

const sameDates = computed(() => {
  if (eventDates.value) {
    if (eventDates.value.length == 1) {
      return true;
    }

    const day1 = dayjs(eventDates.value[0]);
    const day2 = dayjs(eventDates.value[1]);

    return day1.isSame(day2, 'day');
  }
  return false;
});

const anyOvernight = computed(() => {
  let hasOvernight = false;

  newEvent.value?.dates.forEach((date) => {
    if (dayjs(date.from).get('hour') >= dayjs(date.to).get('hour')) {
      hasOvernight = true;
    }
  });

  return hasOvernight;
});

const checkOvernight = (date: EventDate) => {
  const hour = dayjs(date.to).get('hour');
  const min = dayjs(date.to).get('minute');

  if (dayjs(date.from).hour() >= dayjs(date.to).hour()) {
    date.isDateOvernight = true;
    date.to = dayjs(date.from).add(1, 'day').set('hour', hour).set('minute', min).toDate();
  } else {
    date.isDateOvernight = false;
    date.to = dayjs(date.from).set('hour', hour).set('minute', min).toDate();
  }
};

const updateDates = () => {
  if (newEvent.value && eventDates.value) {
    // Standardize our Start & End Date w/ their respective times
    let startDate = dayjs(eventDates.value[0]);
    let endDate = eventDates.value[1] ? dayjs(eventDates.value[1]) : startDate;

    const startTime = dayjs(eventFrom.value);
    const endTime = dayjs(eventTo.value);

    startDate = startDate.set('hour', startTime.hour()).set('minute', startTime.minute());
    endDate = endDate.set('hour', endTime.hour()).set('minute', endTime.minute());

    // Get Range
    includedDays.value = [];

    // Use the same time for each day to make sure the last day counts.
    // Otherwise it could get skipped based on timing and how rounding happens.
    let currentDay = startDate;
    const dateArray: EventDate[] = [];

    const endsOvernight = endDate.get('hour') < startDate.get('hour');
    if (endsOvernight) {
      endDate = endDate.add(1, 'day');
    }

    while (currentDay.isBefore(endDate)) {
      let isDateOvernight = false;
      let currentEnd = currentDay.set('hour', endTime.hour()).set('minute', endTime.minute());

      if (currentDay.isAfter(currentEnd)) {
        isDateOvernight = true;
        currentEnd = currentDay
          .add(1, 'day')
          .set('hour', endTime.hour())
          .set('minute', endTime.minute());
      }

      const eventDate = {
        day: currentDay.format('YYYY-MM-DD'),
        from: currentDay.toDate(),
        to: currentEnd.toDate(),
        isDateOvernight: isDateOvernight,
      };

      dateArray.push(eventDate);
      dateIncludes.value?.push(currentDay.format('YYYY-MM-DD'));
      currentDay = currentDay.add(1, 'day');
    }

    newEvent.value.event_start = startDate.format();
    newEvent.value.event_end = endDate.format();
    newEvent.value.dates = dateArray;
  }
};
</script>
